import React, { useEffect, useState, useCallback } from "react";
import ReactApexChart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import DateRangePicker from "../../molecules/DateRangePicker";
import { IoMdDownload } from "react-icons/io";
import { FiStar } from "react-icons/fi";
import {
  CacheWithExpiration,
  getDateBeforeXDays,
  getISODateFromDate,
} from "../../../lib/utils/funcs";
import ChartLoader from "../../molecules/ChartLoader";
import ChartError from "../../molecules/ChartError";
import NoDataState from "../../molecules/NoDataState";
import { useHospDocData } from "../../../lib/contexts/HospitalDoctorContext";
import {
  getRatingAnalytics,
  downloadRatingAnalytics,
} from "../../../lib/apis/analytics";
import {
  RatingAnalyticsResponse,
  DownloadRequestResponse,
} from "../../../lib/utils/types";
import { format } from 'date-fns';
import { toast } from "react-toastify";

interface RatingGraphProps {
  hospitalId?: string;
  startDate?: string;
  endDate?: string;
  report?:boolean;
  ratingStats?: any;
  onDownloadRequested?: (response: DownloadRequestResponse) => void;
}

const RatingGraph: React.FC<RatingGraphProps> = ({ hospitalId, startDate, endDate, report, ratingStats, onDownloadRequested }) => {
  const { hospitalID: contextHospitalID } = useHospDocData();
  const effectiveHospitalId = hospitalId || contextHospitalID;
  // If props provided, use them, else use internal state
  const [internalStartDate, setInternalStartDate] = useState<Date>(getDateBeforeXDays(7));
  const [internalEndDate, setInternalEndDate] = useState<Date>(getDateBeforeXDays(1));
  const [ratingData, setRatingData] = useState<RatingAnalyticsResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());

  // Update handlers - only disable for report mode with pre-set dates
  const isExternal = !!(startDate && endDate && report);
  
  const effectiveStartDate = React.useMemo(
    () => (isExternal ? new Date(startDate!) : internalStartDate),
    [isExternal, startDate, internalStartDate]
  );
  const effectiveEndDate = React.useMemo(
    () => (isExternal ? new Date(endDate!) : internalEndDate),
    [isExternal, endDate, internalEndDate]
  );
  const handleStartDateChange = (date: Date | null) => {
    console.log("Selected start date:", date ? format(date, 'yyyy-MM-dd') : 'null');
    if (!isExternal && date) setInternalStartDate(date);
  };
  const handleEndDateChange = (date: Date | null) => {
    console.log("Selected end date:", date ? format(date, 'yyyy-MM-dd') : 'null');
    if (!isExternal && date) setInternalEndDate(date);
  };

  const handleRetry = () => {
    fetchRatingDataWithCache();
  };

  const fetchRatingDataWithCache = useCallback(async () => {
    if (!effectiveHospitalId) return;
    setError(null);
    const loadingTimeout = setTimeout(() => {
      setIsLoading(true);
    }, 500);
    const cacheKey = `rating-analytics-${effectiveHospitalId}-${getISODateFromDate(effectiveStartDate)}-${getISODateFromDate(effectiveEndDate)}`;
    try {
      const cachedData = CacheWithExpiration.get(cacheKey);
      if (cachedData) {
        setRatingData(cachedData);
        clearTimeout(loadingTimeout);
        setIsLoading(false);
        return;
      }
      const data = await getRatingAnalytics({
        hospital_id: effectiveHospitalId,
        start_date: getISODateFromDate(effectiveStartDate),
        end_date: getISODateFromDate(effectiveEndDate),
      });
      CacheWithExpiration.set(cacheKey, data, 60);
      setRatingData(data);
    } catch (error) {
      setError("Failed to load chart data. Please try again.");
    } finally {
      clearTimeout(loadingTimeout);
      setIsLoading(false);
    }
  }, [effectiveHospitalId, effectiveStartDate, effectiveEndDate]);

  const handleDownloadRatingAnalytics = async () => {
    if (!effectiveHospitalId || !selectedYear) return;
    const start_date = `${selectedYear}-01-01`;
    const end_date = `${selectedYear}-12-31`;
    const params = {
      hospital_id: effectiveHospitalId,
      start_date,
      end_date,
    };
    try {
      const response = await downloadRatingAnalytics(params);
      if (onDownloadRequested) {
        onDownloadRequested(response);
      } else {
        toast.success("Download request submitted successfully");
      }
    } catch (e: any) {
      toast.error(e?.message || "Failed to request download");
    }
  };

  // Fetch data when component mounts or dependencies change
  useEffect(() => {
    if (effectiveHospitalId && effectiveStartDate && effectiveEndDate && !ratingStats) {
      fetchRatingDataWithCache();
    }else if(ratingStats){
      setRatingData(ratingStats);
    }
  }, [effectiveHospitalId, effectiveStartDate, effectiveEndDate, ratingStats, fetchRatingDataWithCache]);

  // Prepare chart data
  const chartSeries =
    ratingData?.ratings_breakdown?.map((item) => item.count) || [];
  const chartLabels =
    ratingData?.ratings_breakdown?.map((item) => `${item.rating} Star`) || [];

  // Prepare chart data
  const options: ApexOptions = {
    chart: {
      type: "donut",
      height: report ? 250 : 500,
      width: "100%",
    },
    labels: chartLabels,
    colors: [
    "#775DD0",
      "#00E396",
      "#FF4560",
      "#008FFB",
      "#FEB019",
  ],
    plotOptions: {
      pie: {
        startAngle: -90,
        endAngle: 270,
      },
    },
    dataLabels: {
      enabled: true,
      formatter: function (val: number, opts: any) {
        const count = opts.w.globals.series[opts.seriesIndex];
        return `${count}`;
      },
    },
    fill: {
      type: "gradient",
    },
    legend: {
      formatter: function (val, opts) {
        return val + " - " + opts.w.globals.series[opts.seriesIndex];
      },
    },
    tooltip: {
      y: {
        formatter: function (val: number, { seriesIndex }: any) {
          const percentage =
            ratingData?.ratings_breakdown?.[seriesIndex]?.percentage || 0;
          return `${val} ratings (${percentage.toFixed(1)}%)`;
        },
      },
    },
    responsive: [
      {
        breakpoint: 480,
        options: {
          chart: {
            width: 200,
          },
          legend: {
            position: "bottom",
          },
        },
      },
    ],
  };

  return (
    <div className={`w-full ${report ? "h-full" : ""} bg-white rounded-lg shadow flex flex-col justify-between p-4` }>
      <div className={`flex flex-col gap-2 sm:flex-row justify-between items-center ${report ? "hidden" : ""}`}>
        <h2 className="hidden md:flex text-lg font-semibold text-gray-700">Ratings</h2>
        <div className="flex md:hidden flex-row justify-between w-full">
        <h2 className="flex text-lg font-semibold text-gray-700 ">
          Ratings
        </h2>
        <IoMdDownload className="w-8 h-8 text-[#474747] hover:bg-gray-300 p-1 rounded-md cursor-pointer" onClick={handleDownloadRatingAnalytics} />
        </div>
        <div className="flex gap-4">
          <DateRangePicker
            startDate={effectiveStartDate}
            endDate={effectiveEndDate}
            onStartChange={handleStartDateChange}
            onEndChange={handleEndDateChange}
            size="md"
            dateFormat="MMM dd, yyyy"
            inputClassName="border-blue-300 focus:ring-blue-600 text-sm py-1"
            separator="to"
            showIcons={false}
            maxDate={getDateBeforeXDays(1)}
            minDate={new Date(2023, 0, 1)}
          />
          <IoMdDownload className="hidden md:flex w-8 h-8 text-[#474747] hover:bg-gray-300 p-1 rounded-md cursor-pointer" onClick={handleDownloadRatingAnalytics} />
        </div>
      </div>

      {/* Data Loading State */}
      {isLoading && <ChartLoader height={350} containerClassName="" />}

      {/* Error State */}
      {error && !isLoading && (
        <ChartError
          error={error}
          onRetry={handleRetry}
          containerClassName="border-t border-gray-100"
          height={350}
        />
      )}

      {/* Chart Content */}
      {!isLoading && !error && (
        <>
          {chartSeries.length > 0 && chartSeries.some(value => value > 0) ? (
            <ReactApexChart
              options={options}
              series={chartSeries}
              type="donut"
              height={report?200:350}
            />
          ) : (
            <NoDataState
              title="No Rating Data Available"
              description="No rating data found for the selected date range. Try adjusting your date range."
              icon={<FiStar className="w-16 h-16 text-yellow-400" />}
              className="py-24"
              iconContainerClassName="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-full p-6 mb-4"
            />
          )}
        </>
      )}
    </div>
  );
};

export default RatingGraph;
